// Board games in VR. Not seated: the shared join panel over the table.
// Seated: a side panel at your right hand (so it never covers the board),
// following your head height and facing you — lobby options (computer
// level, clock), Start, then status + clocks, Offer / Accept / Decline draw,
// Resign (tap twice), the promotion choice, Leave, How to play and the
// leaderboard. Big buttons, all hand-pokeable. Moves themselves are made on
// the 3D board (BoardTable3D). Table-local, floor origin.

import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { tableAction } from '../gameStore';
import { leaveTable } from '../kit/LobbyKit';
import { VRBtn, VRJoinPanel, VRRulesPanel, VRLeaderboardPanel, vrColors as colors, noRaycast, Surface } from '../kit/vrKit';
import { BOARD_CHAIRS, chairAxes } from './boardLayout';
import { fmtClock, optionValues, useBoardPicker, type BoardGameUI } from './boardGame';
import type { Chair, VRPanelProps } from '../types';

const _h = new THREE.Vector3();

/** A panel beside the chair (sitter's right, a little forward), at chest height, facing the head. */
function SidePanelMount({ chair, children }: { chair: Chair; children: React.ReactNode }) {
  const ref = useRef<THREE.Group>(null);
  const { camera } = useThree();
  const { fx, fz, rx, rz } = chairAxes(chair);
  const x = chair.x + fx * 0.28 + rx * 0.52, z = chair.z + fz * 0.28 + rz * 0.52;
  const settled = useRef(false);
  useFrame(() => {
    const g = ref.current;
    if (!g?.parent) return;
    camera.getWorldPosition(_h);
    g.parent.worldToLocal(_h);
    const ty = THREE.MathUtils.clamp(_h.y - 0.3, 0.75, 1.6);
    g.position.x = x; g.position.z = z;
    g.position.y = settled.current && Math.abs(ty - g.position.y) < 0.4 ? g.position.y + (ty - g.position.y) * 0.08 : ty;
    settled.current = true;
    const dx = _h.x - x, dz = _h.z - z;
    g.rotation.set(-Math.atan2(_h.y - g.position.y, Math.max(0.2, Math.hypot(dx, dz))) * 0.8, Math.atan2(dx, dz), 0, 'YXZ');
  });
  return <group ref={ref}>{children}</group>;
}

export function BoardVRPanel(props: VRPanelProps & { ui: BoardGameUI }) {
  const { ui, def, pid, worldId, entry, table, seat } = props;
  const [rules, setRules] = useState(false);
  const [board, setBoard] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const st = table.state;
  const g = st.g;
  const playing = st.phase === 'playing' && !!g;
  const pick = useBoardPicker(pid, playing && seat >= 0 ? ui.rules(g, seat) : null);
  const [, force] = useState(0);
  const timed = playing && !!ui.clocks && !!ui.clocks(g, 0);
  useEffect(() => {
    if (!timed) return;
    const t = window.setInterval(() => force((n) => n + 1), 500);
    return () => window.clearInterval(t);
  }, [timed]);
  const nMoves = g ? ui.moves(g).length : 0;
  useEffect(() => { setConfirm(false); }, [nMoves, st.phase]);

  if (seat < 0) return <VRJoinPanel def={def} pid={pid} entry={entry} worldId={worldId} />;

  const W = 0.5;
  let H = 0.42;
  const act = (type: string) => void tableAction(pid, 'move', { move: { type } });
  const opp = 1 - seat;
  const oppName = st.seats[opp]?.name ?? def.seatNames?.[opp] ?? 'Opponent';
  let body: React.ReactNode = null;

  if (st.phase === 'lobby') {
    const vals = optionValues(ui, st);
    const n = st.seats.filter(Boolean).length;
    const full = st.seats.every(Boolean);
    H = 0.25 + ui.options.length * 0.11;
    body = <>
      {ui.options.map((o, k) => (
        <group key={o.key} position={[0, H / 2 - 0.135 - k * 0.11, 0]}>
          <Text raycast={noRaycast} position={[-W / 2 + 0.025, 0.04, 0.004]} anchorX="left" fontSize={0.016} color={colors.muted}>{o.label}</Text>
          {o.values.map((v, i) => (
            <VRBtn key={v.v} name={`vr-board-opt-${o.key}-${v.v}`} label={v.label} x={-W / 2 + 0.085 + i * 0.16} y={0} w={0.15} h={0.055} fontSize={0.016}
              active={vals[o.key] === v.v} disabled={entry.busy} onPress={() => void tableAction(pid, 'configure', { options: { [o.key]: v.v } })} />
          ))}
        </group>
      ))}
      <Text raycast={noRaycast} position={[0, -H / 2 + 0.12, 0.004]} fontSize={0.016} color={colors.muted}>
        {full ? 'Both seats taken — ready' : `${n}/2 seated · a friend can sit, or play the computer`}
      </Text>
      <VRBtn name="vr-board-bot" label="Play computer" x={-0.115} y={-H / 2 + 0.06} w={0.21} h={0.06} fontSize={0.016}
        disabled={full || entry.busy} onPress={() => void tableAction(pid, 'fill_bots')} />
      <VRBtn name="vr-board-start" label="Start" x={0.115} y={-H / 2 + 0.06} w={0.21} h={0.06} active={full} disabled={!full || entry.busy}
        onPress={() => void tableAction(pid, 'start')} />
    </>;
  } else if (g) {
    const res = ui.result(g);
    const turn = ui.turn(g);
    const offer = ui.drawOffer(g);
    const clocks = ui.clocks ? ui.clocks(g, Date.now()) : null;
    const status = res ? res.text : pick.promo ? 'Promote to…' : turn === seat ? (pick.path.length ? 'Tap a highlighted square' : 'Your move — tap a piece') : `${oppName} to move`;
    body = <>
      <Text raycast={noRaycast} position={[0, H / 2 - 0.085, 0.004]} fontSize={0.019} maxWidth={W - 0.04} textAlign="center" color={res || turn === seat ? colors.teal : colors.text}>{status}</Text>
      {clocks && (
        <Text raycast={noRaycast} position={[0, H / 2 - 0.125, 0.004]} fontSize={0.017} color={colors.text}>
          {`You ${fmtClock(clocks[seat])}   ·   ${oppName.slice(0, 12)} ${fmtClock(clocks[opp])}`}
        </Text>
      )}
      {playing && pick.promo && ui.promoChoices ? (
        ui.promoChoices.map((c, i) => (
          <VRBtn key={c.v} name={`vr-board-panel-promo-${c.v}`} label={c.label} x={-0.165 + i * 0.11} y={-0.01} w={0.1} h={0.06} fontSize={0.015}
            onPress={() => pick.choosePromo(c.v)} />
        ))
      ) : playing ? <>
        {offer === opp ? <>
          <VRBtn name="vr-board-accept" label="Accept draw" x={-0.115} y={0.0} w={0.21} h={0.06} active disabled={entry.busy} onPress={() => act('accept_draw')} />
          <VRBtn name="vr-board-decline" label="Decline" x={0.115} y={0.0} w={0.21} h={0.06} disabled={entry.busy} onPress={() => act('decline_draw')} />
        </> : (
          <VRBtn name="vr-board-offer" label={offer === seat ? 'Draw offered' : 'Offer draw'} x={-0.115} y={0.0} w={0.21} h={0.06} disabled={offer === seat || entry.busy} onPress={() => act('offer_draw')} />
        )}
        {offer !== opp && (confirm
          ? <VRBtn name="vr-board-resign-yes" label="Tap to resign" x={0.115} y={0.0} w={0.21} h={0.06} active disabled={entry.busy} onPress={() => act('resign')} />
          : <VRBtn name="vr-board-resign" label="Resign" x={0.115} y={0.0} w={0.21} h={0.06} disabled={entry.busy} onPress={() => setConfirm(true)} />)}
        {pick.path.length > 0 && <VRBtn name="vr-board-cancel" label="Cancel pick" x={0} y={-0.075} w={0.2} h={0.05} fontSize={0.015} onPress={pick.clear} />}
      </> : <>
        <VRBtn name="vr-board-rematch" label="Rematch" x={-0.115} y={0.0} w={0.21} h={0.06} active disabled={entry.busy} onPress={() => void tableAction(pid, 'rematch')} />
        <VRBtn name="vr-game-leaders-over" label="Leaderboard" x={0.115} y={0.0} w={0.21} h={0.06} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
      </>}
    </>;
  }

  return (
    <SidePanelMount chair={def.chairs[seat] ?? BOARD_CHAIRS[seat]}>
      <Surface width={W} height={H} color={colors.panel} />
      {body}
      <VRBtn label="Leave" x={W / 2 - 0.06} y={H / 2 - 0.035} w={0.1} h={0.045} fontSize={0.014} onPress={() => leaveTable(pid)} />
      <VRBtn name="vr-game-howto" label="?" x={W / 2 - 0.14} y={H / 2 - 0.035} w={0.05} h={0.045} fontSize={0.016} active={rules} onPress={() => { setBoard(false); setRules((v) => !v); }} />
      <VRBtn name="vr-game-leaders" label="Leaders" x={W / 2 - 0.235} y={H / 2 - 0.035} w={0.13} h={0.045} fontSize={0.014} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
      <Text raycast={noRaycast} position={[-W / 2 + 0.02, H / 2 - 0.035, 0.004]} anchorX="left" fontSize={0.016} color={colors.muted}>
        {`${def.glyph} ${def.seatNames?.[seat] ?? ''}`}
      </Text>
      {rules && <group position={[0, 0, 0.02]}><VRRulesPanel def={def} y={-0.31} onClose={() => setRules(false)} /></group>}
      {board && <group position={[0, 0, 0.02]}><VRLeaderboardPanel def={def} worldId={worldId} refreshKey={st.gameId} onClose={() => setBoard(false)} /></group>}
      {entry.error && <Text raycast={noRaycast} position={[0, -H / 2 + 0.02, 0.004]} fontSize={0.013} maxWidth={W - 0.03} textAlign="center" color="#ff9b9b">{entry.error}</Text>}
    </SidePanelMount>
  );
}

export default BoardVRPanel;
